// IE 10 does'nt support window.location.origin
export function getLocationOrigin(): string {
  return (
    window.location.origin ||
    window.location.protocol +
      "//" +
      window.location.hostname +
      (window.location.port ? ":" + window.location.port : "")
  );
}

// get everything after #
export function extractUriRoute(uri: string): string {
  const hashIndex = uri.indexOf("#");
  return hashIndex > -1 ? uri.slice(hashIndex + 1) : "/";
}

export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const day = date.getDate();
  const monthIndex = date.getMonth();
  const year = date.getFullYear();

  return `${day} ${monthNames[monthIndex]} ${year}`;
}

export function guid(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// this is similar to simple setTimeout, but it works in case of computer hybernates
export function setTimeoutSafe(
  callback: () => void,
  interval: number,
  refreshInterval = 1000,
): () => void {
  let prevTime = Date.now();
  let elapsedTime = 0;

  const tickInterval = setInterval(() => {
    const currTime = Date.now();
    elapsedTime += currTime - prevTime;
    prevTime = currTime;

    if (elapsedTime > interval) {
      clearInterval(tickInterval);
      callback();
    }
  }, refreshInterval);

  return function clearTimeoutSafe() {
    clearInterval(tickInterval);
  };
}

// --- URL JOIN ---
// https://github.com/jfromaniello/url-join

function startsWith(str: string, searchString: string) {
  return str.substr(0, searchString.length) === searchString;
}

function normalize(str: string, options: any) {
  if (startsWith(str, "file://")) {
    // make sure file protocol has max three slashes
    str = str.replace(/(\/{0,3})\/*/g, "$1");
  } else {
    // make sure protocol is followed by two slashes
    str = str.replace(/:\//g, "://");

    // remove consecutive slashes
    str = str.replace(/([^:\s%3A])\/+/g, "$1/");
  }

  // remove trailing slash before parameters or hash
  str = str.replace(/\/(\?|&|#[^!])/g, "$1");

  // replace ? in parameters with &
  str = str.replace(/(\?.+)\?/g, "$1&");

  return str;
}

export function urlJoin(...args: string[]) {
  let input = args;
  let options = {};

  if (typeof args[0] === "object") {
    // new syntax with array and options
    input = args[0];
    options = args[1] || {};
  }

  const joined = [].slice.call(input, 0).join("/");
  return normalize(joined, options);
}

export function retryWithTimeouts<T>(
  promiseFactory: () => Promise<T>,
  timeouts: number[],
  onFail: (err: Error, totalAttempts: number, timeout: number) => void = () =>
    null,
): Promise<T> {
  const totalAttempts = timeouts.length;
  return new Promise((resolve, reject) => {
    promiseFactory().then(resolve, err => {
      onFail(err, totalAttempts, timeouts[0]);
      if (!timeouts.length) {
        return reject(err);
      }
      setTimeout(() => {
        retryWithTimeouts(promiseFactory, timeouts.slice(1), onFail).then(
          resolve,
          reject,
        );
      }, timeouts[0]);
    });
  });
}

export function retryNTimes<T>(
  promiseFactory: <T>() => Promise<T>,
  timeout: number,
  attempts = 10,
): Promise<T> {
  let invocationsCount = 0;
  return new Promise((resolve, reject) => {
    function retry() {
      if (invocationsCount === attempts) {
        reject();
        return;
      }
      ++invocationsCount;
      promiseFactory().then(resolve, () =>
        timeout
          ? setTimeout(() => {
              retry();
            }, timeout)
          : retry(),
      );
    }

    retry();
  });
}

export function isChrome(): boolean {
  // @ts-ignore
  const isChromium = window.chrome,
    winNav = window.navigator,
    vendorName = winNav.vendor,
    isOpera = winNav.userAgent.indexOf("OPR") > -1,
    isIEedge = winNav.userAgent.indexOf("Edge") > -1,
    isIOSChrome = winNav.userAgent.match("CriOS");

  if (isIOSChrome) {
    return true;
  } else {
    return (
      isChromium !== null &&
      isChromium !== undefined &&
      vendorName === "Google Inc." &&
      !isOpera &&
      !isIEedge
    );
  }
}

export function makeSimpleId(): string {
  let text = "";
  const possible =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  for (let i = 0; i < 5; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }

  return text;
}
